import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { deleteBoard, copyBoard } from "../../../../services/restAPI/RestApi";
import { updateBoards } from "../../../../util/Scripts";
import EditModal from "./Actions/EditModal";
import { updateBoardHiddenOption } from "../../../../services/restAPI/RestApi";
import ShareBoardModal from "./Actions/ShareBoardModal";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import "./BoardCard.less";
import { Popover, Button, Message, Tag, Card, Avatar, Popconfirm } from "../../../BP2Components";
import { setUserBoards } from "../../../../appRedux/actions/Boards";
import { Icon_Copy, Icon_Delete, Icon_Edit, Icon_Share } from "../../../BP2Components/Icons/icons";

const BoardCard = ({ boardInfo }) => {
   const [loading, setLoading] = useState(false);
   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
   const [isShareModalOpen, setIsShareModalOpen] = useState(false);
   const { boards } = useSelector(({ boards }) => boards);
   const dispatch = useDispatch();
   const intl = useIntl();

   const getDescription = (board) => {
      const tags = Object.keys(board)
         ?.map((infoKey) => {
            switch (infoKey) {
               case "memberType":
                  return (
                     <Tag key={infoKey} className="geekblue-color">
                        {intl.formatMessage({
                           id: `boardsManager.${board.memberType.toLowerCase()}`
                        })}
                     </Tag>
                  );
               case "showInMainNav":
                  if (board.showInMainNav)
                     return (
                        <Tag key={infoKey}>{intl.formatMessage({ id: "boardsManager.visibleInMainNav" })}</Tag>
                     );
                  break;
               case "boardOrder":
                  if (board.boardOrder || board.boardOrder === 0)
                     return (
                        <Tag key={infoKey} className="purple-color">
                           {intl.formatMessage({ id: "boardsManager.boardOrder" }) +
                              ": " +
                              board.boardOrder}
                        </Tag>
                     );
                  break;
               default:
                  break;
            }
         })
         ?.filter((tag) => !!tag);

      return tags?.length && <div className="tags">{tags}</div>;
   };

   const handleBoardDelete = (boardId) => async () => {
      try {
         setLoading(true);
         Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
         await deleteBoard(boardId);

         updateBoards(intl);
      } catch (err) {
         setLoading(false);
         Message.destroy();
         Message.error(intl.formatMessage({ id: "global.errorWhileDeleting" }));
      }
   };

   const handleSave = (board) => async () => {
      try {
         const updatedBoards = [...boards];
         const boardToUpdate = boards.findIndex((updBoard) => updBoard.id === board.id);
         updatedBoards[boardToUpdate].hidden = !updatedBoards[boardToUpdate].hidden;
         dispatch(setUserBoards(updatedBoards));
         updateBoardHiddenOption(board.id, Boolean(updatedBoards[boardToUpdate].hidden));
      } catch (err) {
         Message.destroy();
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   };

   const handleBoardCopy = (boardId) => async () => {
      try {
         Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
         await copyBoard(boardId);

         updateBoards(intl);
      } catch (err) {
         Message.destroy();
         Message.error(intl.formatMessage({ id: "global.errorWhileDeleting" }));
      }
   };
   return (
      <Card
         className="board-card"
         id={boardInfo?.name}
         actions={
            (boardInfo.memberType === "OWNER" || boardInfo.memberType === "MODERATOR") && [
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.shareBoard" })}
                  placement="bottom"
                  trigger="hover"
                  dark={true}
               >
                  <Icon_Share
                     id="shareBoard"
                     onClick={() => setIsShareModalOpen(true)}
                  />
               </Popover>,
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.editBoard" })}
                  placement="bottom"
                  trigger="hover"
                  dark={true}
               >
                  <Icon_Edit id="editBoard" onClick={() => setIsEditModalOpen(true)} />
               </Popover>,
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.deleteBoard" })}
                  placement="bottom"
                  trigger="hover"
                  dark={true}
               >
                  <Popconfirm
                     title={intl.formatMessage({ id: "settings.sureToDelete" })}
                     onConfirm={handleBoardDelete(boardInfo?.id)}
                     placement="top"
                     disabled={loading}
                     id="delete-board-popconfirm"
                  >
                     <i><Icon_Delete id="deleteBoard" /></i>
                  </Popconfirm>
               </Popover>,
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.copyBoard" })}
                  trigger="hover"
                  dark={true}
                  placement="bottom"
               >
                  <Popconfirm
                     title={intl.formatMessage({ id: "settings.sureToCopy" })}
                     onConfirm={handleBoardCopy(boardInfo?.id)}
                     placement="top"
                     disabled={loading}
                     id="copy-board-popconfirm"
                  >
                     <i><Icon_Copy id="copyBoard" /></i>
                  </Popconfirm>
               </Popover>
            ]
         }
         avatar={
            <Avatar shape="square" src={boardInfo?.iconLink}>
               {!boardInfo?.iconLink && intl.formatMessage({ id: "avatar.noIcon" })}
            </Avatar>
         }
         title={
            <>
               <a
                  className="board-card-link"
                  href={`/boards/${boardInfo?.id}/${boardInfo?.firstSheetId}`}
                  data-name-key={boardInfo?.name}
               >
                  {intl.formatMessage({ id: boardInfo?.name || "board.noName" })}
               </a>
            </>
         }
         description={getDescription(boardInfo)}
      >
         <Button onClick={handleSave(boardInfo)} className="visibility-board-wrapper">
            {boardInfo.hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
         </Button>
         <EditModal
            boardInfo={boardInfo}
            visible={isEditModalOpen}
            handleExit={() => setIsEditModalOpen(false)}
         />
         <ShareBoardModal
            boardInfo={boardInfo}
            visible={isShareModalOpen}
            handleExit={() => setIsShareModalOpen(false)}
         />
      </Card>
   );
};
BoardCard.propTypes = {
   boardInfo: PropTypes.object.isRequired
};

export default BoardCard;
